/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the submitted-work screen with per-question outcomes.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects navigation and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { GroupHeading, Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { NameCell, StatusText } from '../../../shared/components/StatusIcon';
import { Toolbar, tableCells, VTable } from '../../../shared/components/VTable';
import { PRACTICE_QUESTIONS } from '../../../shared/lib/demo-data';
import type { RunRecord } from '../../../shared/lib/model';
import { runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

/** The fixture for "what the student picked". */
const PICKED = [1, 0, 0, 1];

export function StudentReview({ run }: { run: RunRecord }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const notify = useWorkspaceUiStore((state) => state.notify);

  return (
    <Page maxWidth={900}>
      <PageHeader
        actions={
          <>
            <button className="bt" type="button" onClick={() => navigate('rep', run.id)}>
              {t('看回顧')}
            </button>
            <button className="bt pri" type="button" onClick={() => notify(t('已開啟訂正'))}>
              {t('開始訂正')}
            </button>
          </>
        }
        meta={`${classes[run.classIndex].name} · ${t(run.dateLabel)} · 已交`}
        title={runName(activities, run)}
      />
      <Toolbar
        left={
          <span className="vsumline">
            {t('得分 ')}
            <b>92</b> <em>·</em> 答對 <b>2</b> / 4 <em>·</em> 要訂正 <b>2</b> 題
          </span>
        }
      />
      <PageBody>
        <VTable
          columns={[
            { heading: t('題目') },
            { heading: t('我的答案') },
            { heading: t('正解') },
            { heading: t('結果') },
          ]}
          rows={PRACTICE_QUESTIONS.map((question, index) => {
            const correct = PICKED[index] === question.correct;
            return {
              key: question.prompt,
              cells: tableCells(
                <NameCell
                  icon={String(index + 1)}
                  text={question.prompt}
                  tone={correct ? '' : 'e'}
                />,
                <span className="vmut">{`你選 ${'ABCD'[PICKED[index]]}`}</span>,
                correct ? (
                  <span className="vmut">-</span>
                ) : (
                  <span className="vmut">{`正解 ${'ABCD'[question.correct]}`}</span>
                ),
                correct ? (
                  <StatusText glyph="done" label={t('答對')} />
                ) : (
                  <StatusText glyph="err" label={t('答錯')} tone="hot" />
                ),
              ),
            };
          })}
          template="minmax(0,1fr) 110px 110px 100px"
        />
        <GroupHeading>{t('老師的話')}</GroupHeading>
        <div className="eqbox" style={{ fontSize: 15, lineHeight: '25px', margin: '0 8px' }}>
          第 2 題與第 3 題都是移項變號，訂正完這兩題就過關。
        </div>
      </PageBody>
    </Page>
  );
}
